import { useMemo, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import { ArrowLeft, Send } from "lucide-react";
import {
  HELP_IMAGES_MAX,
  HELP_INBOX_MESSAGES_MAX,
  HELP_MESSAGE_MAX,
  HELP_NOTE_MAX,
  HELP_SOURCES_MAX,
  helpInboxReportCreateSchema,
  helpScreenKeyForPath,
  type HelpContext,
  type HelpInboxMessage,
  type HelpInboxReportCreate,
} from "@ttc-spectree/toolbox-contract";
import { useCreateHelpInboxReport } from "@/hooks/queries/use-help-inbox";
import type { HelpAttachment, HelpChatMessage } from "@/hooks/use-help-bridge";
import { ApiError } from "@/lib/api/client";
import { useToolboxBridge } from "@/lib/toolbox/bridge";
import { HELP_SCREEN_LABELS, helpDisplayText, helpRolesLabel } from "@/lib/toolbox/help";
import { TOOLBOX_TEXT } from "@/lib/toolbox/type-scale";
import { cn } from "@/lib/utils";

/**
 * Send to Aaron (epic PI-SPEC-SW-E302, feature PI-SPEC-SW-1559, ER-1655
 * Resolved Decision 5; Claude Design "Toolbox Help Mockups" frame H4).
 *
 * One screen inside the Help panel lists everything that will go to the Help
 * inbox: the conversation, each screenshot (removable), who is sending, the
 * screen, role and versions, and an optional note. **Send** stores exactly
 * one report (one multipart POST); **Cancel** (or the back arrow) stores
 * nothing. Limits show only when hit ("Remove one screenshot to send").
 * Screenshots leave the computer only inside a report the person confirmed;
 * unsent conversations are never stored.
 */

// ---------------------------------------------------------------------------
// Pure helpers (exported for tests)
// ---------------------------------------------------------------------------

/**
 * The conversation as the report carries it: every question, and every Help
 * answer as the person saw it (marker stripped; a failed answer as its
 * error line), with the answer's sources. Empty answers are left out. The
 * newest HELP_INBOX_MESSAGES_MAX messages are kept.
 */
export function helpReportConversation(messages: readonly HelpChatMessage[]): HelpInboxMessage[] {
  const out: HelpInboxMessage[] = [];
  for (const message of messages) {
    if (message.role === "person") {
      out.push({ role: "person", text: message.text.slice(0, HELP_MESSAGE_MAX) });
      continue;
    }
    const shown = helpDisplayText(message.text);
    const text = shown.length > 0 ? shown : message.outcome === "failed" ? (message.errorMessage ?? "") : "";
    if (text.trim().length === 0) continue;
    out.push({
      role: "help",
      text: text.slice(0, HELP_MESSAGE_MAX),
      ...(message.sources.length > 0 ? { sources: message.sources.slice(0, HELP_SOURCES_MAX) } : {}),
    });
  }
  return out.slice(-HELP_INBOX_MESSAGES_MAX);
}

/** Every screenshot the person sent with a question in this conversation, oldest first. */
export function helpReportImages(messages: readonly HelpChatMessage[]): HelpAttachment[] {
  return messages.flatMap((message) => (message.role === "person" ? message.images : []));
}

/** True when any answer in the conversation said Help was not sure. */
export function helpConversationNotSure(messages: readonly HelpChatMessage[]): boolean {
  return messages.some((message) => message.role === "help" && message.verdict === "not-sure");
}

/** The context of the newest question (where the person asked), or `fallback`. */
export function helpReportContext(messages: readonly HelpChatMessage[], fallback: HelpContext): HelpContext {
  for (let i = messages.length - 1; i >= 0; i -= 1) {
    const message = messages[i];
    if (message?.role === "person" && message.context) return message.context;
  }
  return fallback;
}

/** "Queue · icentral-nt": the screen and project of a context. */
export function helpContextScreen(context: HelpContext): string {
  const screen = HELP_SCREEN_LABELS[helpScreenKeyForPath(context.route)];
  return context.project ? `${screen} · ${context.project}` : screen;
}

/** "Toolbox 0.6.0 · SpecTree 2.12.0". */
export function helpContextVersions(context: HelpContext): string {
  return context.spectreeVersion
    ? `Toolbox ${context.appVersion} · SpecTree ${context.spectreeVersion}`
    : `Toolbox ${context.appVersion}`;
}

/** "2:14 PM": the local time a report was sent. */
export function helpSentTime(at: Date): string {
  return at.toLocaleTimeString(undefined, { hour: "numeric", minute: "2-digit" });
}

/** The words for the screenshot limit, or null while within it. */
export function helpScreenshotLimitText(count: number): string | null {
  const over = count - HELP_IMAGES_MAX;
  if (over <= 0) return null;
  return over === 1 ? "Remove one screenshot to send." : `Remove ${String(over)} screenshots to send.`;
}

/** The conversation card's line: the first question and Help's answer(s). */
function conversationSummary(conversation: readonly HelpInboxMessage[]): string {
  const questions = conversation.filter((message) => message.role === "person");
  const first = questions[0]?.text.trim() ?? "";
  const quoted = first.length > 90 ? `"${first.slice(0, 87)}…"` : `"${first}"`;
  if (questions.length <= 1) return `${quoted} and Help's answer`;
  const more = questions.length - 1;
  return `${quoted} and ${String(more)} more ${more === 1 ? "question" : "questions"}, with Help's answers`;
}

/** The plain error under the buttons when the send failed. Nothing was stored. */
function sendErrorText(error: unknown): string {
  const base = "This did not send, and nothing was kept.";
  const message = error instanceof ApiError ? error.message.trim() : "";
  if (error instanceof ApiError && error.response.status === 400 && message.length > 0) {
    return `${base} ${/[.!?]$/.test(message) ? message : `${message}.`}`;
  }
  return `${base} Check your connection, then try Send again.`;
}

// ---------------------------------------------------------------------------
// The screen
// ---------------------------------------------------------------------------

const card = "flex flex-col gap-2 rounded-[10px] border border-[#1F1F23] bg-[#111113] p-3";
const eyebrow = TOOLBOX_TEXT.eyebrow;

function RemovableShot({ attachment, onRemove }: { attachment: HelpAttachment; onRemove: () => void }) {
  return (
    <div
      className="relative flex h-[72px] w-[116px] flex-none items-end overflow-hidden rounded-md border border-[#3F3F46] bg-[#141417] p-1.5"
      data-testid="help-send-shot"
    >
      <img src={attachment.previewUrl} alt={attachment.image.name} className="absolute inset-0 h-full w-full object-cover" />
      <span className="relative max-w-full truncate rounded-[3px] bg-[rgba(9,9,11,0.85)] px-1 font-mono text-[10px] text-[#D4D4D8]">
        {attachment.image.name}
      </span>
      <button
        type="button"
        onClick={onRemove}
        aria-label={`Remove ${attachment.image.name}`}
        className="absolute right-1 top-1 flex h-[18px] w-[18px] items-center justify-center rounded-full border border-[#3F3F46] bg-[#09090B] text-[11px] leading-none text-[#D4D4D8] hover:text-white"
        data-testid="help-send-shot-remove"
      >
        ×
      </button>
    </div>
  );
}

export interface HelpSendToAaronProps {
  messages: readonly HelpChatMessage[];
  /** The signed-in person's name ("From"). */
  senderName: string | undefined;
  /** The roles Help sends with a question now; used only when no question carried a context. */
  roles: readonly string[];
  /** Back to the conversation; nothing was stored. */
  onCancel: () => void;
  /** The report is stored; back to the conversation with "Sent to Aaron · <time>". */
  onSent: (at: Date) => void;
}

/**
 * The H4 screen: replaces the panel's body and ask box while open. The
 * panel header stays.
 */
export function HelpSendToAaron({ messages, senderName, roles, onCancel, onSent }: HelpSendToAaronProps) {
  const bridge = useToolboxBridge();
  const { pathname } = useLocation();
  const create = useCreateHelpInboxReport();
  const sendingRef = useRef(false);
  const [note, setNote] = useState("");
  const [removed, setRemoved] = useState<ReadonlySet<string>>(() => new Set());
  const [error, setError] = useState<string | null>(null);

  const conversation = useMemo(() => helpReportConversation(messages), [messages]);
  const allImages = useMemo(() => helpReportImages(messages), [messages]);
  const images = allImages.filter((attachment) => !removed.has(attachment.id));
  const context = useMemo(
    () =>
      helpReportContext(messages, {
        route: pathname.slice(0, 512) || "/",
        pageTitle: HELP_SCREEN_LABELS[helpScreenKeyForPath(pathname)],
        roles: roles.slice(0, 12),
        appVersion: bridge?.app.version ?? "unknown",
      }),
    [messages, pathname, roles, bridge]
  );
  const limitText = helpScreenshotLimitText(images.length);
  const noteAtLimit = note.length >= HELP_NOTE_MAX;
  const sending = create.isPending;

  const send = () => {
    if (sendingRef.current || limitText !== null) return;
    const trimmedNote = note.trim();
    const report: HelpInboxReportCreate = {
      ...(trimmedNote.length > 0 ? { note: trimmedNote.slice(0, HELP_NOTE_MAX) } : {}),
      conversation,
      context,
      helpWasNotSure: helpConversationNotSure(messages),
    };
    const parsed = helpInboxReportCreateSchema.safeParse(report);
    if (!parsed.success) {
      setError("This conversation could not be sent as it is. Start a new conversation and ask again.");
      return;
    }
    sendingRef.current = true;
    setError(null);
    create.mutate(
      { report: parsed.data, images: images.map((attachment) => attachment.image) },
      {
        onSuccess: () => {
          onSent(new Date());
        },
        onError: (cause) => {
          setError(sendErrorText(cause));
        },
        onSettled: () => {
          sendingRef.current = false;
        },
      }
    );
  };

  return (
    <>
      <div
        className="flex min-h-0 flex-1 flex-col gap-3 overflow-y-auto px-4 py-3.5"
        data-testid="help-send-to-aaron-screen"
      >
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={onCancel}
            aria-label="Back to the conversation"
            className="flex h-6 w-6 flex-none items-center justify-center rounded-lg text-muted-foreground hover:bg-white/[0.04] hover:text-[#FAFAFA]"
            data-testid="help-send-back"
          >
            <ArrowLeft className="h-[15px] w-[15px]" aria-hidden="true" />
          </button>
          <h3 className="font-display text-base font-semibold leading-[1.3]">Send this to Aaron</h3>
        </div>
        <span className="text-[13px] leading-[1.55] text-muted-foreground">
          Aaron sees exactly what is listed here, in the Help inbox. Nothing is kept unless you send it.
        </span>

        <div className={card} data-testid="help-send-conversation">
          <span className={eyebrow}>The conversation</span>
          <span className="text-[13px] text-[#FAFAFA]">{conversationSummary(conversation)}</span>
          <span className="text-xs text-muted-foreground">
            {conversation.length === 1 ? "1 message" : `${String(conversation.length)} messages`}
          </span>
          <details className="group">
            <summary className="cursor-pointer text-xs text-[#6CC4FF] hover:text-[#23A4FF]">Show the conversation</summary>
            <ol className="mt-2 flex flex-col gap-2" data-testid="help-send-messages">
              {conversation.map((message, index) => (
                <li key={`${message.role}-${String(index)}`} className="flex flex-col gap-0.5 text-xs leading-[1.5]">
                  <span className={cn("font-semibold", TOOLBOX_TEXT.faint)}>
                    {message.role === "person" ? "You" : "Help"}
                  </span>
                  <span className="whitespace-pre-wrap break-words text-[#E4E4E7]">{message.text}</span>
                </li>
              ))}
            </ol>
          </details>
        </div>

        {allImages.length > 0 && (
          <div className={card} data-testid="help-send-screenshots">
            <span className={eyebrow}>Screenshots · {images.length}</span>
            {images.length > 0 ? (
              <div className="flex flex-wrap gap-2">
                {images.map((attachment) => (
                  <RemovableShot
                    key={attachment.id}
                    attachment={attachment}
                    onRemove={() => {
                      setRemoved((current) => new Set(current).add(attachment.id));
                    }}
                  />
                ))}
              </div>
            ) : (
              <span className="text-xs text-muted-foreground">No screenshots will be sent.</span>
            )}
            {limitText && (
              <span role="alert" className="text-[13px] text-[#FBBF24]" data-testid="help-send-limit">
                {limitText}
              </span>
            )}
          </div>
        )}

        <div className={card} data-testid="help-send-about">
          <span className={eyebrow}>About you and this screen</span>
          <dl className="grid grid-cols-[96px_minmax(0,1fr)] gap-x-3 gap-y-1.5 text-xs">
            <dt className={TOOLBOX_TEXT.faint}>From</dt>
            <dd className="text-[#FAFAFA]">{senderName ?? "You"}</dd>
            <dt className={TOOLBOX_TEXT.faint}>Screen</dt>
            <dd className="text-[#FAFAFA]">{helpContextScreen(context)}</dd>
            <dt className={TOOLBOX_TEXT.faint}>Role</dt>
            <dd className="text-[#FAFAFA]">{helpRolesLabel(context.roles)}</dd>
            <dt className={TOOLBOX_TEXT.faint}>Versions</dt>
            <dd className="text-[#FAFAFA]">{helpContextVersions(context)}</dd>
          </dl>
        </div>

        <label className="flex flex-col gap-1.5">
          <span className="text-xs text-muted-foreground">
            Anything to add? <span className={TOOLBOX_TEXT.faint}>(optional)</span>
          </span>
          <textarea
            value={note}
            maxLength={HELP_NOTE_MAX}
            rows={2}
            onChange={(event) => {
              setNote(event.target.value);
            }}
            className="min-h-10 resize-y rounded-lg border border-[#27272A] bg-[#121215] px-2.5 py-2 text-xs text-[#FAFAFA] outline-none placeholder:text-[#8B8B94] focus:border-[#3F3F46]"
            data-testid="help-send-note"
          />
          {noteAtLimit && (
            <span className="text-xs text-[#FBBF24]" data-testid="help-send-note-limit">
              A note can be up to {HELP_NOTE_MAX.toLocaleString("en-US")} characters.
            </span>
          )}
        </label>
      </div>
      <div className="flex flex-none flex-col gap-2 border-t border-[#1F1F23] px-3 pb-3 pt-2.5">
        {error && (
          <span role="alert" className="text-[13px] leading-[1.5] text-[#FBBF24]" data-testid="help-send-error">
            {error}
          </span>
        )}
        <div className="flex gap-2">
          <button
            type="button"
            onClick={send}
            disabled={sending || limitText !== null}
            className="inline-flex h-[30px] flex-1 items-center justify-center gap-1.5 whitespace-nowrap rounded-lg bg-[#E11837] px-[13px] text-[13px] font-semibold text-white hover:bg-[#C8102E] disabled:cursor-not-allowed disabled:opacity-50"
            data-testid="help-send-confirm"
          >
            <Send className="h-3.5 w-3.5" aria-hidden="true" />
            {sending ? "Sending…" : "Send to Aaron"}
          </button>
          <button
            type="button"
            onClick={onCancel}
            disabled={sending}
            className="inline-flex h-[30px] items-center gap-1.5 whitespace-nowrap rounded-lg border border-[#27272A] bg-[#1A1A1E] px-[11px] text-[13px] font-medium text-[#FAFAFA] hover:bg-[#222226] disabled:opacity-50"
            data-testid="help-send-cancel"
          >
            Cancel
          </button>
        </div>
      </div>
    </>
  );
}
